/**
 * The channels Flashadz can run for a goal, each with the provider it works through and the card the
 * owner sees on the Channels step. A goal keeps its own copy of the card's words once the owner edits
 * them (GoalConfig.channels[id].card); these are only the words a new goal starts with.
 */
export interface ChannelDef {
  label: string;
  sub: string;
  /** The skill in the library that says how this channel is run. */
  skill: string;
  /** Every skill a goal on this channel loads; no other skill, nor its tools, reaches its runs. */
  skills: readonly string[];
  /** The connection it needs (connections page), or null when it needs none. */
  connection: "meta" | "email" | null;
  /** What the connected state is called on the card. */
  connected: string;
  card: { for: string[]; own: string; ask: string };
}

export const CHANNELS = {
  meta: {
    label: "Meta ads",
    sub: "Facebook and Instagram",
    skill: "meta-ads",
    skills: ["meta-ads", "ad-creative", "measurement", "market-research"],
    connection: "meta",
    connected: "Meta connected",
    card: {
      for: ["One campaign, one ad set, a few video ads to test", "Aims at the goal's result, within its daily budget", "An ad stops when it hits the stop rule: a set spend with no result"],
      own: "Reads results every morning and pauses an ad that trips a stop rule.",
      ask: "Launching, adding ads, raising the budget.",
    },
  },
  email: {
    label: "Cold email",
    sub: "Small lists, one market at a time",
    skill: "cold-email",
    skills: ["cold-email", "lead-research", "market-research"],
    connection: "email",
    connected: "Email provider connected",
    card: {
      for: ["A sending address of your own through your email provider, starting slow", "Three emails at most to each person", "Only addresses people published themselves"],
      own: "Builds and checks the list, and pauses on a bounce or a complaint.",
      ask: "Every first email and every reply.",
    },
  },
} as const satisfies Record<string, ChannelDef>;

export type ChannelId = keyof typeof CHANNELS;
export const CHANNEL_ORDER: readonly ChannelId[] = ["meta", "email"];
export const isChannelId = (k: string): k is ChannelId => k in CHANNELS;
/** The skills the channels that are on load, together. */
export const skillsFor = (channels: Record<string, { on: boolean } | boolean | undefined>): Set<string> =>
  new Set(
    Object.entries(channels)
      .filter(([k, c]) => isChannelId(k) && (typeof c === "boolean" ? c : !!c?.on))
      .flatMap(([k]) => CHANNELS[k as ChannelId].skills),
  );

/**
 * A channel a change would add to a goal. A goal runs the channel New goal started it with: a change may
 * switch it off and on again, never add another. Another channel is another goal.
 */
export const addedChannel = (before: Record<string, unknown>, after: Record<string, unknown>): string | null => Object.keys(after).find((k) => !(k in before)) ?? null;

export const ADDED_CHANNEL = "A goal keeps the channel it started with. Start a new goal for another channel.";
